import React from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {TaskSetDrawer} from './TaskSetDrawer';
import {describeEnd, describePattern, formatSetDate} from './describeTaskSet';
import {taskSetColour} from './taskSetColours';

/**
 * Label and value on one baseline-aligned row, label column fixed at 118px so
 * every value starts at the same x. A stack of label-above-value read as a form,
 * which this is not -- it is a record.
 */
const Field = styled.div`
	display: grid;
	grid-template-columns: 118px 1fr;
	align-items: baseline;
	gap: 12px;
	padding: 11px 0;
	border-bottom: 1px solid #f3f3f3;
`;

/** Sentence case and regular weight: the value is the thing being read. */
const FieldLabel = styled.span`
	font-size: 12px;
	color: #7d7d7d;
`;

const FieldValue = styled.span`
	font-size: 13px;
	line-height: 19px;
	color: rgba(0, 0, 0, 0.87);
`;

/** Shown where a value cannot be described, never a fabricated one. */
const Muted = styled(FieldValue)`
	color: #a1a1a1;
`;

const Actions = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	padding-top: 18px;
`;

/**
 * Dashed and grey on purpose: these mark where editing and deleting a series
 * attach without looking like controls that merely happen to be greyed out.
 */
const GhostAction = styled.span`
	height: 28px;
	border: 1px dashed #c3c8d0;
	border-radius: 4px;
	padding: 0 12px;
	display: flex;
	align-items: center;
	font-size: 12px;
	font-weight: 600;
	color: #9aa2b1;
	cursor: not-allowed;
`;

/**
 * Carries the set's colour, the same one its instances' chips carry. min-width: 0
 * lets the chip itself shrink inside the drawer header's title row; the name
 * truncates in its own span below rather than here, so the icon never does.
 */
const NameChip = styled.span<{background: string; foreground: string}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	height: 19px;
	padding: 0 8px 0 6px;
	border-radius: 9.5px;
	background: ${({background}) => background};
	color: ${({foreground}) => foreground};
	font-size: 11px;
	font-weight: 600;
	min-width: 0;
`;

/** Truncated with the full name on hover -- a long set name must not push the
    chip past the header and crowd the close button. */
const NameChipText = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

interface ConfigTaskSet {
	readonly id: string;
	readonly name?: string | null;
	readonly frequency?: string | null;
	readonly intervalLength?: number | null;
	readonly weekdays?: readonly (string | null)[] | null;
	readonly monthlyWeekday?: string | null;
	readonly monthlyMode?: string | null;
	readonly dayOfMonth?: number | null;
	readonly weekOrdinal?: number | null;
	readonly boundType?: string | null;
	readonly boundCount?: number | null;
	readonly boundEndDate?: string | null;
	readonly materialisedTo?: string | null;
	readonly seedDate?: string | null;
	readonly instanceCount?: number | null;
	readonly exceptionCount?: number | null;
	readonly parentPhase?: {readonly id: string; readonly name?: string | null} | null;
}

export interface TaskSetConfigViewProps {
	/** Null closes the view. Both access points set the same piece of page state. */
	taskSetId: string | null;
	project: unknown;
	onClose: () => void;
}

const configFragment = graphql`
	fragment TaskSetConfigView_project on ProjectType {
		taskSets(first: 1000) {
			edges {
				node {
					id
					name
					frequency
					intervalLength
					weekdays
					monthlyWeekday
					monthlyMode
					dayOfMonth
					weekOrdinal
					boundType
					boundCount
					boundEndDate
					materialisedTo
					seedDate
					instanceCount
					exceptionCount
					parentPhase {
						id
						name
					}
				}
			}
		}
	}
`;

/**
 * A set's configuration, read-only.
 *
 * Reached from two places -- the chip on an instance and `View` in the manage
 * panel -- which both set one piece of page state, so the two paths cannot drift
 * into two different surfaces.
 *
 * Read-only is a decision, not a placeholder. Building the addressable identity and
 * this surface now is what stops editing the series, and deleting it, having to
 * introduce a surface from scratch later; `Edit set` and `Delete set` render
 * disabled to mark where they attach.
 *
 * Carried attributes -- estimate, role, assignee, description, deliverable -- are
 * not shown, because the set does not store them. They are stamped onto each
 * instance at creation and live on the tasks; a value shown here would either be
 * invented or read from one arbitrary instance.
 */
export const TaskSetConfigView = ({taskSetId, project, onClose}: TaskSetConfigViewProps) => {
	const intl = useIntl();
	const data = useFragment(configFragment, project as never) as {
		taskSets?: {edges?: ReadonlyArray<{node?: ConfigTaskSet | null} | null> | null} | null;
	} | null;

	const set = (data?.taskSets?.edges || []).map(edge => edge?.node).find(node => !!node && node.id === taskSetId) || null;

	// The drawer stays mounted through its own slide-out, so it is rendered even
	// with nothing selected; the body is only filled when a set is in hand.
	const pattern = set ? describePattern(intl, set) : null;
	// The end date alone: the Instances row below already states the count, so
	// leading with it here repeated the number and left a bare separator dot.
	const bound = set ? describeEnd(intl, set) : null;
	const none = intl.formatMessage({id: 'task_set.none'});

	const phaseLine = !set
		? null
		: set.parentPhase && set.parentPhase.name
		? // The phase the set was CREATED in. Labelling this "Phase" flatly would
		  // assert that every instance is in it, which stops being true as soon as
		  // instances can be moved apart.
		  intl.formatMessage({id: 'task_set.created_in_phase'}, {phase: set.parentPhase.name})
		: intl.formatMessage({id: 'task_set.created_outside_phase'});

	const colour = taskSetColour(set?.id);

	return (
		<TaskSetDrawer
			isOpen={!!set}
			// The set name rides in the chip beside this heading, so the heading names
			// the surface rather than repeating the name.
			title={intl.formatMessage({id: 'task_set.about'})}
			titleSize={17}
			titleAdornment={
				set ? (
					<NameChip background={colour.background} foreground={colour.foreground} data-cy="task-set-config-name">
						<svg
							width="11"
							height="11"
							viewBox="0 0 16 16"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.8"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
							style={{flexShrink: 0}}
						>
							<path d="M3 8a5 5 0 0 1 8.4-3.7M13 8a5 5 0 0 1-8.4 3.7" />
							<path d="M11.6 2.2v2.4H9.2M4.4 13.8v-2.4h2.4" />
						</svg>
						<NameChipText title={set.name ?? undefined}>{set.name}</NameChipText>
					</NameChip>
				) : null
			}
			onClose={onClose}
		>
			{set ? (
				<>
					<Field data-cy="task-set-config-pattern">
						<FieldLabel>{intl.formatMessage({id: 'task_set.pattern'})}</FieldLabel>
						{pattern === null ? <Muted>{none}</Muted> : <FieldValue>{pattern}</FieldValue>}
					</Field>
					{/* First instance before Ends: the two are a matched pair of plain
					    dates, and a start reads before its end. */}
					{set.seedDate ? (
						<Field data-cy="task-set-config-first-instance">
							<FieldLabel>{intl.formatMessage({id: 'task_set.first_instance'})}</FieldLabel>
							<FieldValue>{formatSetDate(intl, set.seedDate) ?? set.seedDate}</FieldValue>
						</Field>
					) : null}
					<Field data-cy="task-set-config-bound">
						<FieldLabel>{intl.formatMessage({id: 'task_set.ends'})}</FieldLabel>
						{bound === null ? <Muted>{none}</Muted> : <FieldValue>{bound}</FieldValue>}
					</Field>
					<Field data-cy="task-set-config-phase">
						<FieldLabel>{intl.formatMessage({id: 'task_set.phase_label'})}</FieldLabel>
						<FieldValue>{phaseLine}</FieldValue>
					</Field>
					<Field data-cy="task-set-config-instances">
						<FieldLabel>{intl.formatMessage({id: 'task_set.instances'})}</FieldLabel>
						<FieldValue>{set.instanceCount ?? 0}</FieldValue>
					</Field>
					<Field data-cy="task-set-config-exceptions">
						<FieldLabel>{intl.formatMessage({id: 'task_set.exception_list'})}</FieldLabel>
						{set.exceptionCount ? <FieldValue>{set.exceptionCount}</FieldValue> : <Muted>{none}</Muted>}
					</Field>
					{/* Inert, and drawn so they read as not-yet-built rather than as
					    controls that happen to be greyed out. */}
					<Actions>
						<GhostAction data-cy="task-set-edit">{intl.formatMessage({id: 'task_set.edit_set'})}</GhostAction>
						<GhostAction data-cy="task-set-delete">{intl.formatMessage({id: 'task_set.delete_set'})}</GhostAction>
					</Actions>
				</>
			) : null}
		</TaskSetDrawer>
	);
};
